import type { TrackingEvent, Payload } from '../types'

// Import cohesion-utils types to get the Window.tagular declaration
import type {} from '@redventures/cohesion-utils'

export interface BeamConfig {
  tagular?: boolean
  console?: boolean
}

/**
 * Generic tracking service that can be used across the monorepo
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class TrackingService {
  private static config: BeamConfig = {
    tagular: true,
    console:
      typeof process !== 'undefined' && process.env
        ? process.env['NODE_ENV'] === 'development'
        : false,
  }

  /**
   * Configure the tracking service
   */
  static configure(config: Partial<BeamConfig>) {
    this.config = { ...this.config, ...config }
  }

  /**
   * Send data to Tagular via the beam function.
   *
   * @returns whether the payload was handed to Tagular. False means the page
   * has no `tagular` global yet (the Cohesion snippet has not attached) or the
   * beam threw — in both cases no row left the page.
   */
  private static beam(payload: Payload): boolean {
    if (typeof window === 'undefined' || !window.tagular) return false

    try {
      window.tagular('beam', payload)
      return true
    } catch (error) {
      if (this.config.console) {
        console.error('Tagular beam failed:', error)
      }
      return false
    }
  }

  /**
   * Track any tracking event.
   *
   * @returns whether the event reached Tagular. Callers that only fire and
   * forget can keep ignoring this; it exists for callers whose own state
   * depends on the row having been sent — a once-per-session gate must not be
   * consumed by an event that was silently dropped.
   *
   * Tagular being disabled by config counts as not sent, since no row leaves
   * the page in that case either.
   */
  static track(event: TrackingEvent): boolean {
    const eventPayload: Payload = {
      '@type': event.type,
      ...event.properties,
    }

    try {
      const beamed = this.config.tagular ? this.beam(eventPayload) : false

      // Console logging for development
      if (this.config.console) {
        console.log('Tracking Event:', {
          eventName: event.eventName,
          type: event.type,
          properties: event.properties,
        })
      }

      return beamed
    } catch (error) {
      if (this.config.console) {
        console.error('Tracking failed:', error)
      }
      return false
    }
  }

  /**
   * Track a custom event with raw data
   */
  static trackCustom(
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties: any,
    eventType?: string,
  ): void {
    const customEvent: TrackingEvent = {
      eventName,
      type: eventType || 'custom',
      properties,
    }

    this.track(customEvent)
  }
}
